import { useEffect, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { AiFillCheckCircle } from "react-icons/ai";

import Tooltip from "../Tooltip";

export default function AutoCompleteComboBox({
  labelName = "Quick Filter",
  tooltipVerbiage,
  inputPlaceholderText = "Search by keyword",
  options = [],
  invalidMessage = "Nothing Found",
  selectedOption,
  setSelectedOption,
}) {
  const [selected, setSelected] = useState(selectedOption);
  const [query, setQuery] = useState("");
  const [showOption, setShowOption] = useState(false);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) =>
          option.displayAs
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  useEffect(() => {
    setSelected(
      options.find(
        (option) =>
          option.value ===
          (typeof selectedOption === "object"
            ? selectedOption.value
            : selectedOption)
      )
    );
  }, [options, selectedOption]);

  return (
    <Combobox
      value={selected}
      onChange={(value) => {
        setSelectedOption(value);
        setSelected(value);
        setShowOption(false);
      }}
    >
      {({ activeIndex, activeOption, disabled, open }) => (
        <div className="relative mt-1">
          {labelName && (
            <div className="flex items-center mb-1">
              <Combobox.Label>{labelName}</Combobox.Label>
              {tooltipVerbiage && (
                <Tooltip xMargin="mx-0" content={tooltipVerbiage} />
              )}
            </div>
          )}

          <div className="relative">
            <Combobox.Input
              placeholder={inputPlaceholderText}
              // perform search
              onChange={(event) => {
                setQuery(event.target.value);
                setShowOption(true);
              }}
              // clear on focus
              onClick={(e) => {
                e.target.value = "";
                setShowOption(true);
              }}
              // re-apply value when not selected
              onBlur={(e) => {
                e.target.value = selected?.displayAs ?? options[0]?.displayAs;
                if (
                  !activeOption?.value ||
                  activeOption?.value === selected?.value
                )
                  setShowOption(false);
              }}
              // control what gets to be on display when selected an option
              displayValue={(option) => option?.displayAs}
            />
          </div>
          <Transition
            show={showOption}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              static
              className="absolute mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg sm:text-sm z-10"
            >
              {filteredOptions.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none py-2 px-4">
                  {invalidMessage}
                </div>
              ) : (
                <>
                  {filteredOptions.map(
                    (option) =>
                      option?.displayAs && (
                        <Combobox.Option
                          key={option.value}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-8 pr-4 ${
                              active ? "bg-ui-blue text-white" : "text-body"
                            }`
                          }
                          value={option}
                        >
                          {({ selected, active }) => (
                            <span
                              className={`block truncate ${
                                selected ? "font-semibold" : "font-normal"
                              }`}
                            >
                              {option?.displayAs}
                              {selected && (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? "text-white" : "text-ui-blue"
                                  }`}
                                >
                                  <AiFillCheckCircle className="w-4 h-4" />
                                </span>
                              )}
                            </span>
                          )}
                        </Combobox.Option>
                      )
                  )}
                </>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}
